(() => {
  const selector = 'details.dt-details, details.nova-footer-submenu, details.nova-article-toc, details.facets__disclosure-vertical, details.facets__disclosure, details.nova-collection-description, .nova-policy-nav > details, cart-note > details, .drawer__footer details';
  const controllers = new WeakMap();
  const active = new Set();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class DisclosureMotion {
    constructor(element, trigger, isDetails) {
      this.element = element;
      this.trigger = trigger;
      this.isDetails = isDetails;
      this.content = isDetails ? trigger.nextElementSibling : element;
      if (isDetails) this.content = this.content.querySelector('.drawer-details') || this.content;
      this.desired = this.isOpen();
      if (isDetails) element.addEventListener('toggle', () => {
        if (this.animation && !element.open) this.finish(false);
        if (!this.animation) trigger.setAttribute('aria-expanded', String(element.open));
      });
    }

    isOpen() {
      return this.isDetails ? this.element.open : this.element.classList.contains('nova-footer-menu--open');
    }

    show(open) {
      if (this.isDetails) this.element.open = open;
      else this.element.classList.toggle('nova-footer-menu--open', open);
    }

    cancel() {
      const animation = this.animation;
      this.animation = null;
      if (animation) {
        animation.onfinish = animation.oncancel = null;
        animation.cancel();
      }
    }

    finish(open) {
      this.cancel();
      this.desired = open;
      this.show(open);
      this.trigger.setAttribute('aria-expanded', String(open));
      if (this.original) {
        for (const [name, value, priority] of this.original) {
          if (value) this.element.style.setProperty(name, value, priority);
          else this.element.style.removeProperty(name);
        }
        this.original = null;
        this.content.inert = this.wasInert;
      }
      active.delete(this);
    }

    toggle() {
      const open = !(this.animation ? this.desired : this.isOpen());
      this.desired = open;
      if (!open && this.content.contains(document.activeElement)) this.trigger.focus({preventScroll: true});
      if (motion.matches || typeof this.element.animate !== 'function') {
        this.finish(open);
        return;
      }
      const start = this.element.getBoundingClientRect().height;
      const previousOpacity = this.animation ? getComputedStyle(this.content).opacity : null;
      const previousTransform = this.animation ? getComputedStyle(this.content).transform : null;
      this.cancel();
      if (!this.original) {
        this.original = ['height', 'overflow', 'box-sizing'].map(name => [name, this.element.style.getPropertyValue(name), this.element.style.getPropertyPriority(name)]);
        this.wasInert = this.content.inert;
      }
      const popup = ['absolute', 'fixed'].includes(getComputedStyle(this.content).position);
      this.show(true);
      this.content.inert = !open || this.wasInert;
      this.trigger.setAttribute('aria-expanded', String(open));
      if (popup) {
        const animation = this.content.animate({
          opacity: [previousOpacity ?? (open ? 0 : 1), open ? 1 : 0],
          transform: [previousTransform ?? (open ? 'translateY(6px)' : 'translateY(0)'), open ? 'translateY(0)' : 'translateY(6px)']
        }, {duration: open ? 240 : 200, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both'});
        this.animation = animation;
        active.add(this);
        animation.onfinish = () => { if (this.animation === animation) this.finish(open); };
        animation.oncancel = () => { if (this.animation === animation) this.finish(open); };
        return;
      }
      this.element.style.boxSizing = 'border-box';
      this.element.style.height = 'auto';
      this.element.style.overflow = 'hidden';
      this.show(open);
      const end = this.element.getBoundingClientRect().height;
      this.show(true);
      this.element.style.height = `${start}px`;
      const animation = this.element.animate({height: [`${start}px`, `${end}px`]}, {
        duration: open ? 320 : 260,
        easing: 'cubic-bezier(.22,.61,.36,1)',
        fill: 'both'
      });
      this.animation = animation;
      active.add(this);
      animation.onfinish = () => { if (this.animation === animation) this.finish(open); };
      animation.oncancel = () => { if (this.animation === animation) this.finish(open); };
    }
  }

  function controller(element, trigger, isDetails) {
    if (!controllers.has(element)) controllers.set(element, new DisclosureMotion(element, trigger, isDetails));
    return controllers.get(element);
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const summary = event.target.closest('summary');
    const details = summary?.parentElement;
    if (details?.matches(selector)) {
      if (event.target.closest('a, button, input, select, textarea') && event.target !== summary) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      controller(details, summary, true).toggle();
      return;
    }
    const trigger = event.target.closest('.index-footer.footer-style3 .nova-footer-accordion-toggle');
    const menu = trigger && document.getElementById(trigger.getAttribute('aria-controls'));
    if (!menu) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    controller(menu, trigger, false).toggle();
  }, true);

  document.addEventListener('keyup', event => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const details = event.target.closest(selector);
    if (!details || !details.open) return;
    event.stopImmediatePropagation();
    const instance = controller(details, details.querySelector('summary'), true);
    if (instance.animation && !instance.desired) return;
    instance.toggle();
    instance.trigger.focus({preventScroll: true});
  }, true);

  motion.addEventListener('change', () => {
    if (motion.matches) [...active].forEach(instance => instance.finish(instance.desired));
  });
  window.addEventListener('resize', () => [...active].forEach(instance => instance.finish(instance.desired)), {passive: true});
})();
